/**
 * Parcerias V1 — Drill-down (cliente or parceiro)
 *
 * Live data via `useParceriasDetails` (monumenta-trpc parcerias router, MONU-74).
 * No mock data — backend is deployed.
 *
 * Reached from /financial/parcerias landing → /financial/parcerias/<axis>/<id>
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft, HeartHandshake, Building2, Receipt, Search, X, Calendar,
  AlertTriangle, Settings2, Sparkles, Plus, Trash2,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import {
  useParceriasDetails,
  useParceriasConfig,
  useUpsertParceriasConfig,
  useRemoveParceriasConfig,
} from '@/api/parcerias-hooks'
import {
  PeriodPresetSelector,
  calculatePeriodDates,
  type PeriodDates,
} from '../dre/components/PeriodPresetSelector'
import type {
  ParceriasAxis,
  ParceriaLineItem,
  ParceriasConfigEntry,
} from '@/api/parcerias-router.types'

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrencyBR(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}M`
  }
  if (abs >= 1_000) {
    return `R$ ${(value / 1_000).toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}K`
  }
  return formatCurrencyBR(value)
}

function formatProtheusDate(s: string | null): string {
  if (!s || s.length !== 8) return '—'
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`
}

/** Convert ISO date string YYYY-MM-DD → Protheus YYYYMMDD. */
function isoToProtheus(iso: string): string {
  return iso.replace(/-/g, '')
}

/** Left-pad numeric Protheus code with zeros to fixed CHAR width.
 *  Display layer strips them ("2518"); backend Zod expects padded ("002518"). */
function padProtheusCode(value: string, width: 2 | 6): string {
  return /^\d+$/.test(value) ? value.padStart(width, '0') : value
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StatPill({
  label,
  value,
  hint,
  sensitive = false,
  loading = false,
}: {
  label: string
  value: string
  hint?: string
  sensitive?: boolean
  loading?: boolean
}) {
  return (
    <div className="bg-[#1E2024] border border-[#2A2D34] rounded-lg px-3 py-2">
      <div className="text-[10px] text-brand-gray uppercase tracking-wide font-medium">
        {label}
      </div>
      {loading ? (
        <div className="mt-1 h-4 w-16 rounded bg-brand-dark-lighter animate-pulse" />
      ) : (
        <div className="text-sm font-semibold text-white mt-0.5">
          {sensitive ? (
            <SensitiveValue mask="R$ ••••••">{value}</SensitiveValue>
          ) : (
            value
          )}
        </div>
      )}
      {hint && !loading && (
        <div className="text-[10px] text-brand-gray/60 mt-0.5">{hint}</div>
      )}
    </div>
  )
}

type SortKey = 'dtPagamento' | 'descPago' | 'descBaixado' | 'descFallback' | 'fornecedorNome' | 'clienteNome' | 'rNota'
type SortDir = 'asc' | 'desc'
interface SortState {
  key: SortKey
  dir: SortDir
}

function SortIndicator({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span className="text-brand-gray/30 ml-1">↕</span>
  return <span className="text-brand-primary ml-1">{dir === 'asc' ? '↑' : '↓'}</span>
}

function HeaderCell({
  children,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  children: React.ReactNode
  sortKey?: SortKey
  sort: SortState | null
  onSort?: (key: SortKey) => void
  align?: 'left' | 'right' | 'center'
}) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
  if (!sortKey) {
    return (
      <th
        className={`${alignClass} px-3 py-2 text-[10px] font-semibold text-brand-gray uppercase tracking-wide`}
      >
        {children}
      </th>
    )
  }
  const isActive = sort?.key === sortKey
  return (
    <th
      className={`${alignClass} px-3 py-2 text-[10px] font-semibold text-brand-gray uppercase tracking-wide`}
    >
      <button
        type="button"
        onClick={() => onSort?.(sortKey)}
        className="inline-flex items-center hover:text-white transition-colors cursor-pointer"
      >
        {children}
        <SortIndicator active={isActive} dir={sort?.dir ?? 'asc'} />
      </button>
    </th>
  )
}

function sortItems(
  items: ParceriaLineItem[],
  sort: SortState | null,
): ParceriaLineItem[] {
  if (!sort) return items
  const { key, dir } = sort
  const mult = dir === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    const aVal: string | number = a[key] ?? ''
    const bVal: string | number = b[key] ?? ''
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * mult
    }
    return String(aVal).localeCompare(String(bVal), 'pt-BR', { numeric: true }) * mult
  })
}

function TableSkeleton({ axis }: { axis: ParceriasAxis }) {
  const cols = axis === 'parceiro' ? 9 : 8
  return (
    <div className="bg-[#1E2024] border border-[#2A2D34] rounded-xl overflow-hidden">
      <div className="p-4 space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            {Array.from({ length: cols }).map((__, j) => (
              <div key={j} className="h-3 flex-1 rounded bg-brand-dark-lighter" />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Admin modal — manage fallback configs scoped to this drill-down entity
// ============================================================================

interface ParceriasConfigModalProps {
  axis: ParceriasAxis
  /** Scoped id — A1_COD when axis=cliente, "A2_COD-A2_LOJA" when axis=parceiro. */
  id: string
  /** Display name of the entity being scoped (cliente or parceiro). */
  scopeName: string
  /** Counter-party suggestions extracted from the loaded items so the user
   *  can quickly mark from the visible drill set without typing. */
  counterpartyOptions: Array<{
    parceiroCodigo: string
    parceiroLoja: string
    parceiroNome: string
    clienteCodigo: string
    clienteNome: string
  }>
  onClose: () => void
}

function ParceriasConfigModal({
  axis,
  id,
  scopeName,
  counterpartyOptions,
  onClose,
}: ParceriasConfigModalProps) {
  // Scope the listConfig query to the current drill-down entity
  const listFilter = useMemo(() => {
    if (axis === 'cliente') return { clienteCodigo: id }
    const [codigo = '', loja = ''] = id.split('-')
    return { parceiroCodigo: codigo, parceiroLoja: loja }
  }, [axis, id])

  const { data: configsResp, isLoading: loadingConfigs } = useParceriasConfig(listFilter)
  const configs = configsResp?.items ?? []

  const upsert = useUpsertParceriasConfig()
  const remove = useRemoveParceriasConfig()

  const [observacao, setObservacao] = useState('')
  const [selectedCounterparty, setSelectedCounterparty] = useState<string>('')

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  // Unique counterparty options — filter out ones already configured
  const availableCounterparties = useMemo(() => {
    const configured = new Set(
      configs.map((c) =>
        axis === 'cliente'
          ? `${c.parceiroCodigo}-${c.parceiroLoja}`
          : c.clienteCodigo,
      ),
    )
    const seen = new Set<string>()
    return counterpartyOptions.filter((opt) => {
      const key =
        axis === 'cliente'
          ? `${opt.parceiroCodigo}-${opt.parceiroLoja}`
          : opt.clienteCodigo
      if (seen.has(key) || configured.has(key)) return false
      seen.add(key)
      return true
    })
  }, [axis, configs, counterpartyOptions])

  const handleAdd = useCallback(() => {
    if (!selectedCounterparty) return
    let parceiroCodigo: string
    let parceiroLoja: string
    let parceiroNome: string | undefined
    let clienteCodigo: string
    let clienteNome: string | undefined

    if (axis === 'cliente') {
      // selectedCounterparty is "parceiroCodigo-parceiroLoja"
      const opt = counterpartyOptions.find(
        (o) => `${o.parceiroCodigo}-${o.parceiroLoja}` === selectedCounterparty,
      )
      if (!opt) return
      parceiroCodigo = opt.parceiroCodigo
      parceiroLoja = opt.parceiroLoja
      parceiroNome = opt.parceiroNome
      clienteCodigo = id
      clienteNome = scopeName
    } else {
      // axis=parceiro: selectedCounterparty is clienteCodigo
      const opt = counterpartyOptions.find((o) => o.clienteCodigo === selectedCounterparty)
      if (!opt) return
      const [codigo = '', loja = ''] = id.split('-')
      parceiroCodigo = codigo
      parceiroLoja = loja
      parceiroNome = scopeName
      clienteCodigo = opt.clienteCodigo
      clienteNome = opt.clienteNome
    }

    upsert.mutate(
      {
        parceiroCodigo,
        parceiroLoja,
        clienteCodigo,
        parceiroNome,
        clienteNome,
        observacao: observacao.trim() || undefined,
      },
      {
        onSuccess: () => {
          setSelectedCounterparty('')
          setObservacao('')
        },
      },
    )
  }, [axis, counterpartyOptions, id, observacao, scopeName, selectedCounterparty, upsert])

  const handleRemove = useCallback(
    (entry: ParceriasConfigEntry) => {
      remove.mutate({
        parceiroCodigo: entry.parceiroCodigo,
        parceiroLoja: entry.parceiroLoja,
        clienteCodigo: entry.clienteCodigo,
      })
    },
    [remove],
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-[10vh] px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#1E2024] border border-[#2A2D34] rounded-2xl w-full max-w-2xl shadow-2xl mb-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#2A2D34]">
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles size={18} className="text-brand-primary shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-bold text-white truncate">
                Configurar parcerias fallback
              </h2>
              <p className="text-xs text-brand-gray mt-0.5">
                {axis === 'cliente' ? 'Cliente' : 'Parceiro'}: {scopeName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-brand-gray hover:text-white transition-colors cursor-pointer p-1"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="text-xs text-brand-gray-light leading-relaxed">
            Marcações ativas classificam NFs de parcela única como parceria
            fallback quando Protheus não tem sibling pra calcular pago/baixado.
          </div>

          {/* Existing configs */}
          <div>
            <div className="text-[10px] text-brand-gray uppercase tracking-wider font-semibold mb-2">
              Marcações ativas
            </div>
            {loadingConfigs ? (
              <div className="h-10 rounded bg-brand-dark-lighter animate-pulse" />
            ) : configs.length === 0 ? (
              <p className="text-xs text-brand-gray/60">
                Nenhuma marcação ainda — adicione abaixo.
              </p>
            ) : (
              <ul className="space-y-2">
                {configs.map((c) => (
                  <li
                    key={`${c.parceiroCodigo}-${c.parceiroLoja}#${c.clienteCodigo}`}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg bg-[#171a1e] border border-[#2A2D34]"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        {axis === 'cliente'
                          ? c.parceiroNome || `Parceiro ${c.parceiroCodigo}-${c.parceiroLoja}`
                          : c.clienteNome || `Cliente ${c.clienteCodigo}`}
                      </div>
                      <div className="text-[10px] font-mono text-brand-gray/60">
                        {c.parceiroCodigo}-{c.parceiroLoja} → {c.clienteCodigo}
                      </div>
                      {c.observacao && (
                        <div className="text-xs text-brand-gray-light mt-1 italic">
                          {c.observacao}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(c)}
                      disabled={remove.isPending}
                      className="shrink-0 text-brand-gray/50 hover:text-red-400 transition-colors cursor-pointer p-1 disabled:opacity-50"
                      aria-label="Remover marcação"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add new */}
          <div className="border-t border-[#2A2D34] pt-4">
            <div className="text-[10px] text-brand-gray uppercase tracking-wider font-semibold mb-3">
              Adicionar marcação
            </div>
            {availableCounterparties.length === 0 ? (
              <p className="text-xs text-brand-gray/60">
                Todas as contrapartes visíveis já estão marcadas, ou nenhuma foi
                encontrada nesta janela. Use o filtro de período para expor mais.
              </p>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedCounterparty}
                  onChange={(e) => setSelectedCounterparty(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-[#171a1e] border border-[#2A2D34] rounded-lg text-white focus:outline-none focus:border-brand-primary/50"
                >
                  <option value="">
                    Escolha {axis === 'cliente' ? 'um parceiro' : 'um cliente'}...
                  </option>
                  {availableCounterparties.map((opt) => {
                    const value =
                      axis === 'cliente'
                        ? `${opt.parceiroCodigo}-${opt.parceiroLoja}`
                        : opt.clienteCodigo
                    const label =
                      axis === 'cliente'
                        ? `${opt.parceiroNome} (${value})`
                        : `${opt.clienteNome} (${value})`
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  })}
                </select>

                <input
                  type="text"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  maxLength={500}
                  placeholder="Observação (opcional)"
                  className="w-full px-3 py-2 text-sm bg-[#171a1e] border border-[#2A2D34] rounded-lg text-white placeholder:text-brand-gray/40 focus:outline-none focus:border-brand-primary/50"
                />

                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!selectedCounterparty || upsert.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-brand-primary/15 text-brand-primary hover:bg-brand-primary/25 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus size={13} />
                  {upsert.isPending ? 'Salvando...' : 'Adicionar'}
                </button>

                {upsert.error && (
                  <p className="text-xs text-red-400">
                    {upsert.error.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ParceriasDrillDown({
  axis,
  id,
}: {
  axis: ParceriasAxis
  id: string
}) {
  const titlePrefix = axis === 'cliente' ? 'Parcerias · Cliente' : 'Parcerias · Parceiro'
  usePageTitle(titlePrefix)
  useAuth()
  const navigate = useNavigate()

  // Period filter (defaults to YTD — same as landing).
  const defaultDates = useMemo(() => calculatePeriodDates('ytd'), [])
  const [periodStartDate, setPeriodStartDate] = useState(defaultDates.startDate)
  const [periodEndDate, setPeriodEndDate] = useState(defaultDates.endDate)

  const handlePeriodChange = useCallback((dates: PeriodDates) => {
    setPeriodStartDate(dates.startDate)
    setPeriodEndDate(dates.endDate)
  }, [])

  const { data: details, isLoading, error } = useParceriasDetails({
    axis,
    id,
    dataInicial: isoToProtheus(periodStartDate),
    dataFinal: isoToProtheus(periodEndDate),
  })

  const items = details?.items ?? []
  const displayName = details?.displayName ?? (axis === 'cliente' ? `Cliente ${id}` : `Parceiro ${id}`)
  const totalRepasse = details?.totalRepasse ?? 0

  const [searchQuery, setSearchQuery] = useState('')
  const [sort, setSort] = useState<SortState | null>({ key: 'dtPagamento', dir: 'desc' })
  const [configModalOpen, setConfigModalOpen] = useState(false)

  // Build counterparty options from the loaded items so the admin modal can
  // offer a dropdown of fornecedores (when axis=cliente) or clientes (when
  // axis=parceiro) seen in this drill-down. Codes are re-padded to Protheus
  // CHAR(6)/CHAR(2) widths — the listing layer strips zeros for display, but
  // the upsert API expects the padded form to validate via Zod regex.
  const counterpartyOptions = useMemo(
    () =>
      items.map((i) => ({
        parceiroCodigo: padProtheusCode(i.fornecedorCodigo, 6),
        parceiroLoja: padProtheusCode(i.fornecedorLoja, 2),
        parceiroNome: i.fornecedorNome,
        clienteCodigo: padProtheusCode(i.clienteCodigo, 6),
        clienteNome: i.clienteNome,
      })),
    [items],
  )

  // Pad the scoped id similarly so the modal can use it as the "fixed" side
  // of the (parceiro, cliente) tuple when adding marks.
  const scopedPaddedId = useMemo(() => {
    if (axis === 'cliente') return padProtheusCode(id, 6)
    const [codigo = '', loja = ''] = id.split('-')
    return `${padProtheusCode(codigo, 6)}-${padProtheusCode(loja, 2)}`
  }, [axis, id])

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    const q = searchQuery.toLowerCase()
    return items.filter(
      (i) =>
        i.fornecedorNome.toLowerCase().includes(q) ||
        i.clienteNome.toLowerCase().includes(q) ||
        i.nf.includes(q) ||
        i.naturezaDesc.toLowerCase().includes(q) ||
        i.centroCustoDesc.toLowerCase().includes(q),
    )
  }, [searchQuery, items])

  const sortedItems = useMemo(() => sortItems(filteredItems, sort), [filteredItems, sort])

  const handleSort = (key: SortKey) => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, dir: 'desc' }
      if (current.dir === 'desc') return { key, dir: 'asc' }
      return null
    })
  }

  const distinctCount = useMemo(
    () =>
      new Set(
        items.map((i) =>
          axis === 'cliente'
            ? `${i.fornecedorCodigo}-${i.fornecedorLoja}`
            : i.clienteCodigo,
        ),
      ).size,
    [items, axis],
  )

  const lastPaymentDate = items.length > 0 ? items[0]?.dtPagamento ?? null : null

  const totalDescBaixado = useMemo(
    () => items.reduce((sum, item) => sum + item.descBaixado, 0),
    [items],
  )

  const totalDescFallback = useMemo(
    () => items.reduce((sum, item) => sum + item.descFallback, 0),
    [items],
  )

  return (
    <div className="space-y-6">
      {/* Breadcrumb / back */}
      <button
        type="button"
        onClick={() => navigate({ to: '/financial/parcerias' })}
        className="inline-flex items-center gap-1.5 text-xs text-brand-gray hover:text-brand-primary transition-colors cursor-pointer"
      >
        <ArrowLeft size={13} />
        Voltar para Parcerias
      </button>

      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          {axis === 'cliente' ? (
            <Building2 size={22} className="text-brand-primary" />
          ) : (
            <HeartHandshake size={22} className="text-brand-primary" />
          )}
          <h1 className="text-2xl font-bold font-display text-white">
            {displayName}
          </h1>
          <span className="px-2 py-0.5 rounded-md bg-brand-dark-lighter text-brand-gray text-[10px] font-bold uppercase tracking-wider">
            {axis === 'cliente' ? 'Cliente' : 'Parceiro'}
          </span>
          <span className="text-xs text-brand-gray/60 font-mono">{id}</span>

          {/* Admin: open fallback config modal */}
          <button
            type="button"
            onClick={() => setConfigModalOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-dark-lighter text-brand-gray hover:text-brand-primary hover:bg-brand-primary/10 border border-brand-dark-lighter hover:border-brand-primary/30 transition-colors cursor-pointer"
          >
            <Settings2 size={13} />
            Configurar parcerias
          </button>
        </div>
        <p className="text-sm text-brand-gray mt-1">
          Lançamentos de parceria no período (data de pagamento — cash basis).
        </p>
      </div>

      {/* Period filter */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide min-w-[80px]">
          Período
        </span>
        <PeriodPresetSelector
          startDate={periodStartDate}
          endDate={periodEndDate}
          onPeriodChange={handlePeriodChange}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-400">Erro ao carregar lançamentos</h3>
            <p className="text-xs text-brand-gray-light mt-1">
              {error instanceof Error ? error.message : 'Não foi possível conectar ao Protheus.'}
            </p>
          </div>
        </div>
      )}

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatPill
          label="Parceria Total"
          value={formatCompact(totalRepasse + totalDescBaixado + totalDescFallback)}
          sensitive
          loading={isLoading}
        />
        <StatPill
          label="Parceria Paga"
          value={formatCompact(totalRepasse)}
          hint="precisa de NF de terceiros"
          sensitive
          loading={isLoading}
        />
        <StatPill
          label="Parceria Baixada"
          value={formatCompact(totalDescBaixado)}
          hint="só sobe no gerencial"
          sensitive
          loading={isLoading}
        />
        <StatPill
          label="Parceria Fallback"
          value={formatCompact(totalDescFallback)}
          hint="marcação manual"
          sensitive
          loading={isLoading}
        />
        <StatPill label="Lançamentos" value={String(items.length)} loading={isLoading} />
        <StatPill
          label={axis === 'cliente' ? 'Parceiros distintos' : 'Clientes distintos'}
          value={String(distinctCount)}
          loading={isLoading}
        />
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gray/50 pointer-events-none"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por NF, fornecedor, natureza..."
          className="w-full pl-9 pr-9 py-2 text-sm bg-[#1E2024] border border-[#2A2D34] rounded-lg text-white placeholder:text-brand-gray/40 focus:outline-none focus:border-brand-primary/50 transition-colors"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-brand-gray/50 hover:text-white transition-colors cursor-pointer p-0.5"
            aria-label="Limpar busca"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Table — loading / live */}
      {isLoading ? (
        <TableSkeleton axis={axis} />
      ) : (
        <div className="bg-[#1E2024] border border-[#2A2D34] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#171a1e] border-b border-[#2A2D34]">
                <tr>
                  <HeaderCell sortKey="dtPagamento" sort={sort} onSort={handleSort}>
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={11} />
                      Pagamento
                    </span>
                  </HeaderCell>
                  <HeaderCell sort={sort}>NF</HeaderCell>
                  <HeaderCell sortKey="fornecedorNome" sort={sort} onSort={handleSort}>
                    Parceiro
                  </HeaderCell>
                  {axis === 'parceiro' && (
                    <HeaderCell sortKey="clienteNome" sort={sort} onSort={handleSort}>
                      Cliente
                    </HeaderCell>
                  )}
                  <HeaderCell sort={sort}>Centro de Custo</HeaderCell>
                  <HeaderCell sortKey="descBaixado" sort={sort} onSort={handleSort} align="right">
                    Desc. Baixado
                  </HeaderCell>
                  <HeaderCell sortKey="descPago" sort={sort} onSort={handleSort} align="right">
                    Desc. Pago
                  </HeaderCell>
                  <HeaderCell sortKey="descFallback" sort={sort} onSort={handleSort} align="right">
                    Fallback
                  </HeaderCell>
                  <HeaderCell sortKey="rNota" sort={sort} onSort={handleSort} align="right">
                    R$ Nota
                  </HeaderCell>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => (
                  <tr
                    key={`${item.nf}-${item.parcela}-${item.fornecedorCodigo}`}
                    className="border-b border-[#2A2D34] last:border-b-0 hover:bg-[#22252B] transition-colors"
                  >
                    <td className="px-3 py-3 text-brand-gray-light whitespace-nowrap">
                      {formatProtheusDate(item.dtPagamento)}
                    </td>
                    <td className="px-3 py-3 text-white font-mono text-xs">
                      {item.nf}
                      {item.parcela && item.parcela !== '1' && (
                        <span className="text-brand-gray/50 ml-1">/{item.parcela}</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-white">{item.fornecedorNome}</div>
                      <div className="text-[10px] text-brand-gray/50 font-mono">
                        {item.fornecedorCodigo}-{item.fornecedorLoja}
                      </div>
                    </td>
                    {axis === 'parceiro' && (
                      <td className="px-3 py-3 text-brand-gray-light">
                        {item.clienteNome}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="text-brand-gray-light text-xs">
                        {item.centroCustoDesc || '—'}
                      </div>
                      <div className="text-[10px] text-brand-gray/50 font-mono">
                        {item.centroCusto}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {item.descBaixado > 0 ? (
                        <div className="text-brand-gray-light">
                          <SensitiveValue mask="R$ ••••••">
                            {formatCurrencyBR(item.descBaixado)}
                          </SensitiveValue>
                        </div>
                      ) : (
                        <span className="text-brand-gray/30">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {item.descPago > 0 ? (
                        <div className="text-yellow-400 font-semibold">
                          <SensitiveValue mask="R$ ••••••">
                            {formatCurrencyBR(item.descPago)}
                          </SensitiveValue>
                        </div>
                      ) : (
                        <span className="text-brand-gray/30">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {item.descFallback > 0 ? (
                        <div className="text-brand-primary font-semibold inline-flex items-center gap-1">
                          <Sparkles size={11} />
                          <SensitiveValue mask="R$ ••••••">
                            {formatCurrencyBR(item.descFallback)}
                          </SensitiveValue>
                        </div>
                      ) : (
                        <span className="text-brand-gray/30">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="text-white">
                        <SensitiveValue mask="R$ ••••••">
                          {formatCurrencyBR(item.rNota)}
                        </SensitiveValue>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={axis === 'parceiro' ? 9 : 8}
                      className="px-3 py-16 text-center text-brand-gray/60 text-sm"
                    >
                      <Receipt
                        size={28}
                        className="text-brand-gray/30 mx-auto mb-3"
                      />
                      Nenhum lançamento de parceria encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Admin: fallback config modal */}
      {configModalOpen && (
        <ParceriasConfigModal
          axis={axis}
          id={scopedPaddedId}
          scopeName={displayName}
          counterpartyOptions={counterpartyOptions}
          onClose={() => setConfigModalOpen(false)}
        />
      )}
    </div>
  )
}
